/**
 * El paso 3: "¿Es esto?"
 *
 * Lo que la IA leyo del encabezado, y la cuenta que importa: cuanto suman las
 * lineas contra el total que declara el comprobante. Si no coincide, no se
 * sigue. Es el chequeo mas barato contra una linea mal leida —un 87,80 en vez
 * de 8,78— y el unico que agarra ese error antes de que entre al stock.
 *
 * El proveedor se resuelve aca. Si el documento dice "ELIT S.A." y no esta
 * cargado, se crea con un click y sin salir del wizard: el primer escalon del
 * matching (codigo del proveedor dentro del proveedor) depende de eso.
 */
import { useState, type ReactNode } from "react";
import { Pencil } from "lucide-react";
import { formatearNumero } from "@tora/shared";

import { Campo, CampoCantidad } from "@/components/catalogo/Campos";
import { Importe, InputImporte } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { aNumero, type Proveedor } from "@/lib/tipos";
import { formatearFecha } from "@/lib/utils";

import type { CambiosEncabezado } from "./consultas";
import {
  TEXTO_COMPROBANTE,
  TIPOS_COMPROBANTE,
  esManual,
  unidadesDelRemito,
  verificarTotal,
  type Compra,
  type FilaCompra,
  type TipoComprobante,
} from "./logica";

const SIN_PROVEEDOR = "ninguno";

export interface EncabezadoProps {
  compra: Compra;
  filas: ReadonlyArray<FilaCompra>;
  proveedores: ReadonlyArray<Proveedor>;
  guardando: boolean;
  onProveedor: (id: number) => void;
  onCrearProveedor: (nombre: string) => void;
  onCorregir: (cambios: CambiosEncabezado) => void;
  onContinuar: () => void;
}

/** "ELIT S.A." -> "Elit": el nombre corto con el que el local lo llama. */
export function nombreCortoProveedor(detectado: string): string {
  const limpio = detectado
    .replace(/\b(S\.?A\.?C?\.?|S\.?R\.?L\.?|S\.?A\.?S\.?|SOCIEDAD ANONIMA|LTDA\.?)\b/gi, "")
    .replace(/[.,]+$/g, "")
    .trim();
  const base = limpio === "" ? detectado.trim() : limpio;
  return base
    .toLowerCase()
    .split(/\s+/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

export function Encabezado({
  compra,
  filas,
  proveedores,
  guardando,
  onProveedor,
  onCrearProveedor,
  onCorregir,
  onContinuar,
}: EncabezadoProps): JSX.Element {
  const [editando, setEditando] = useState(false);
  const proveedor = proveedores.find((p) => p.id === compra.proveedor_id) ?? null;
  const verificacion = verificarTotal(filas, compra.total_declarado);
  const unidades = unidadesDelRemito(filas);
  const moneda = compra.moneda ?? filas[0]?.moneda ?? "ARS";

  // Se puede seguir aunque el total no cierre: el aviso de abajo manda a
  // revisar los productos en el paso siguiente, y antes ese paso no se abria
  // (habia que borrar el total para seguir). El freno esta al aplicar, en la
  // revision, que dice que corregir.
  const puedeSeguir = proveedor !== null;

  return (
    <div className="space-y-4">
      <Card padding="normal" className="space-y-4">
        <TituloSeccion
          acciones={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setEditando((v) => !v)}
              disabled={guardando}
            >
              <Pencil aria-hidden="true" />
              {editando ? "Dejar de editar" : "Corregir"}
            </Button>
          }
        >
          ¿Es esto?
        </TituloSeccion>

        {editando ? (
          <FormularioEncabezado
            compra={compra}
            guardando={guardando}
            onGuardar={(cambios) => {
              onCorregir(cambios);
              setEditando(false);
            }}
          />
        ) : (
          <dl className="grid grid-cols-1 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
            <Dato
              titulo="Comprobante"
              valor={`${compra.tipo_comprobante ? TEXTO_COMPROBANTE[compra.tipo_comprobante] : "—"} ${compra.numero_comprobante ?? ""}`.trim()}
            />
            <Dato
              titulo="Fecha"
              valor={compra.fecha_comprobante ? formatearFecha(compra.fecha_comprobante) : "—"}
            />
            <Dato titulo="Remito" valor={compra.numero_remito ?? "—"} />
            <Dato titulo="Moneda" valor={compra.moneda ?? "—"} />
            <Dato
              titulo="Cotización"
              valor={
                compra.cotizacion === null ? "—" : formatearNumero(aNumero(compra.cotizacion), 2)
              }
            />
            <Dato
              titulo="Total declarado"
              valor={
                compra.total_declarado === null ? (
                  "—"
                ) : (
                  <Importe monto={aNumero(compra.total_declarado)} moneda={moneda} />
                )
              }
            />
          </dl>
        )}

        {/* La cuenta. */}
        {filas.length === 0 ? (
          <Aviso tono="warning">
            No hay ningún producto todavía. Los cargás a mano en el paso siguiente.
          </Aviso>
        ) : verificacion.cierra ? (
          <Aviso tono="success" data-testid="verificacion-total">
            {filas.length} {filas.length === 1 ? "producto" : "productos"},{" "}
            {formatearNumero(unidades, 0)} {unidades === 1 ? "unidad" : "unidades"}, suman{" "}
            <Importe monto={verificacion.calculado} moneda={moneda} className="font-semibold" />
            {verificacion.declarado === null
              ? ". El documento no declara un total contra el que comparar."
              : " — coincide con el total del comprobante."}
          </Aviso>
        ) : (
          <Aviso tono="destructive" titulo="El total no cierra" data-testid="verificacion-total">
            <p>
              {filas.length === 1 ? "El producto suma" : `Los ${filas.length} productos suman`}{" "}
              <Importe monto={verificacion.calculado} moneda={moneda} className="font-semibold" /> y
              el comprobante dice{" "}
              <Importe
                monto={verificacion.declarado ?? 0}
                moneda={moneda}
                className="font-semibold"
              />
              : hay una diferencia de{" "}
              <Importe
                monto={Math.abs(verificacion.diferencia)}
                moneda={moneda}
                className="font-semibold"
              />
              .
            </p>
            <p className="text-muted-foreground">
              Casi siempre es una línea mal leída (un punto decimal corrido) o una línea que falta.
              Revisá los productos en el paso siguiente o corregí el total con «Corregir». Si la
              diferencia es un flete, dejá el total vacío y poné lo que le debés al revisar.
            </p>
          </Aviso>
        )}
      </Card>

      <Card padding="normal" className="space-y-3">
        <TituloSeccion>Proveedor</TituloSeccion>
        {proveedor ? (
          <p className="text-sm">
            <span className="font-medium">{proveedor.nombre}</span>
            {compra.proveedor_detectado ? (
              <span className="text-muted-foreground">
                {" "}
                · el documento dice «{compra.proveedor_detectado}»
              </span>
            ) : null}
          </p>
        ) : (
          <p className="text-sm">
            {compra.proveedor_detectado ? (
              <>
                El documento dice «<strong>{compra.proveedor_detectado}</strong>»
                {compra.cuit_detectado ? ` (CUIT ${compra.cuit_detectado})` : ""}. No lo tenés
                cargado como proveedor.
              </>
            ) : esManual(compra) ? (
              "Esta compra se cargó a mano. Elegí el proveedor de la lista."
            ) : (
              "El documento no dice quién es el proveedor."
            )}
          </p>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          {!proveedor && compra.proveedor_detectado ? (
            <Button
              type="button"
              disabled={guardando}
              onClick={() =>
                onCrearProveedor(nombreCortoProveedor(compra.proveedor_detectado ?? ""))
              }
            >
              Crear proveedor «{nombreCortoProveedor(compra.proveedor_detectado)}»
            </Button>
          ) : null}
          <div className="sm:min-w-64">
            <Campo
              label={proveedor ? "Cambiar de proveedor" : "O elegí uno de la lista"}
              id="proveedor-compra"
            >
              <Select
                value={proveedor ? String(proveedor.id) : SIN_PROVEEDOR}
                onValueChange={(v) => {
                  if (v !== SIN_PROVEEDOR) onProveedor(Number(v));
                }}
                disabled={guardando}
              >
                <SelectTrigger id="proveedor-compra">
                  <SelectValue placeholder="Elegí el proveedor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SIN_PROVEEDOR}>Sin proveedor</SelectItem>
                  {proveedores.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>
          </div>
        </div>
        {!proveedor ? (
          <p className="text-xs text-muted-foreground">
            Sin proveedor no corre el emparejamiento por código de proveedor, que es el más
            confiable, y los productos nuevos quedan sin proveedor.
          </p>
        ) : null}
      </Card>

      <div className="flex justify-end">
        <Button type="button" onClick={onContinuar} disabled={!puedeSeguir || guardando}>
          Continuar con los productos
        </Button>
      </div>
    </div>
  );
}

function Dato({ titulo, valor }: { titulo: string; valor: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{titulo}</dt>
      <dd className="truncate font-medium tabular-nums">{valor === "" ? "—" : valor}</dd>
    </div>
  );
}

/** Los campos del encabezado, editables. Lo que el operador ve en el papel. */
function FormularioEncabezado({
  compra,
  guardando,
  onGuardar,
}: {
  compra: Compra;
  guardando: boolean;
  onGuardar: (cambios: CambiosEncabezado) => void;
}): JSX.Element {
  const [tipo, setTipo] = useState<TipoComprobante | "">(compra.tipo_comprobante ?? "");
  const [numero, setNumero] = useState(compra.numero_comprobante ?? "");
  const [remito, setRemito] = useState(compra.numero_remito ?? "");
  const [fecha, setFecha] = useState(compra.fecha_comprobante ?? "");
  const [moneda, setMoneda] = useState<"ARS" | "USD" | "">(compra.moneda ?? "");
  const [cotizacion, setCotizacion] = useState<number | null>(
    compra.cotizacion === null ? null : aNumero(compra.cotizacion),
  );
  const [total, setTotal] = useState<number | null>(
    compra.total_declarado === null ? null : aNumero(compra.total_declarado),
  );
  // Un total que no se entiende no se guarda como "sin total": se frena.
  const [totalInvalido, setTotalInvalido] = useState(false);

  function guardar(): void {
    onGuardar({
      tipo_comprobante: tipo === "" ? null : tipo,
      numero_comprobante: numero.trim() === "" ? null : numero.trim(),
      numero_remito: remito.trim() === "" ? null : remito.trim(),
      fecha_comprobante: /^\d{4}-\d{2}-\d{2}$/.test(fecha.trim()) ? fecha.trim() : null,
      moneda: moneda === "" ? null : moneda,
      cotizacion,
      total_declarado: total,
    });
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Campo label="Tipo" id="enc-tipo">
          <Select
            value={tipo === "" ? "SIN" : tipo}
            onValueChange={(v) => setTipo(v === "SIN" ? "" : (v as TipoComprobante))}
          >
            <SelectTrigger id="enc-tipo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SIN">—</SelectItem>
              {TIPOS_COMPROBANTE.map((t) => (
                <SelectItem key={t} value={t}>
                  {TEXTO_COMPROBANTE[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Campo>
        <Campo label="Número" id="enc-numero">
          <Input
            id="enc-numero"
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            placeholder="00027-00410877"
            autoComplete="off"
          />
        </Campo>
        <Campo label="Remito" id="enc-remito">
          <Input
            id="enc-remito"
            value={remito}
            onChange={(e) => setRemito(e.target.value)}
            placeholder="00037-00000514"
            autoComplete="off"
          />
        </Campo>
        <Campo label="Fecha" id="enc-fecha">
          <Input
            id="enc-fecha"
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
          />
        </Campo>
        <Campo label="Moneda" id="enc-moneda">
          <Select
            value={moneda === "" ? "SIN" : moneda}
            onValueChange={(v) => setMoneda(v === "SIN" ? "" : (v as "ARS" | "USD"))}
          >
            <SelectTrigger id="enc-moneda">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SIN">—</SelectItem>
              <SelectItem value="ARS">ARS</SelectItem>
              <SelectItem value="USD">USD</SelectItem>
            </SelectContent>
          </Select>
        </Campo>
        {/* La cotizacion es un numero, no plata: sin simbolo ni chip. */}
        <CampoCantidad
          label="Cotización"
          id="enc-cotiz"
          valor={cotizacion}
          onValorChange={setCotizacion}
          placeholder="1.535,00"
          ayuda="El dólar que te tomó el proveedor. Si hay dólares en juego, sin él no se aplica."
        />
        {/* El total declarado ES plata: acepta "1.234,56" y nunca lo vacia en
            silencio. La moneda es la del comprobante; si todavia no se eligio,
            se muestra en pesos hasta que se elija. */}
        <div className="sm:col-span-3">
          <InputImporte
            id="enc-total"
            label="Total declarado (con IVA)"
            valor={total}
            onValorChange={setTotal}
            onInvalidoChange={setTotalInvalido}
            moneda={moneda === "" ? "ARS" : moneda}
            placeholder="77,77"
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="button" onClick={guardar} disabled={totalInvalido} cargando={guardando}>
          Guardar el encabezado
        </Button>
      </div>
    </div>
  );
}
